
import React, { useState, useEffect } from 'react';
import { StaffUser, StaffRole } from '../types';
import { CaseDatabase } from '../services/db';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    role: StaffRole.PARALEGAL,
    password: 'password123'
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const data = await CaseDatabase.getAllStaff();
      setStaff(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error("Failed to load staff list:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStaff.name || !newStaff.email) {
      alert("Missing Information: Name and email are required.");
      return;
    }

    setActionLoading('adding');
    const newUser: StaffUser = {
      id: crypto.randomUUID(),
      name: newStaff.name,
      email: newStaff.email,
      role: newStaff.role,
      password: newStaff.password,
      createdAt: new Date().toISOString()
    };
    
    try {
      await CaseDatabase.saveStaff(newUser);
      setIsAdding(false);
      setNewStaff({ name: '', email: '', role: StaffRole.PARALEGAL, password: 'password123' });
      await loadStaff();
    } catch (err) {
      console.error("Add staff failed:", err);
      alert("Failed to create staff account.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (id === currentUser?.id) {
      alert("Security Protocol: You cannot revoke your own access privileges.");
      return;
    }

    const targetStaff = staff.find(s => s.id === id);
    const confirmation = window.confirm(
      `CRITICAL ACTION: Are you sure you want to PERMANENTLY revoke access for ${targetStaff?.name || 'this staff member'}?`
    );

    if (confirmation) {
      setActionLoading(`deleting-${id}`);
      try {
        await CaseDatabase.deleteStaff(id);
        setStaff(prev => prev.filter(member => member.id !== id));
      } catch (err) {
        console.error("Delete action failed:", err);
        alert("System Error: Failed to revoke access.");
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleRoleChange = async (targetUser: StaffUser, newRole: StaffRole) => {
    if (targetUser.id === currentUser?.id && newRole !== StaffRole.ADMIN && newRole !== StaffRole.SUPER_ADMIN) {
      alert("Security Protocol: You cannot downgrade your own administrative status.");
      return;
    }

    setActionLoading(`updating-${targetUser.id}`);
    const updated = { ...targetUser, role: newRole };
    try {
      await CaseDatabase.saveStaff(updated);
      setStaff(prev => prev.map(m => m.id === targetUser.id ? updated : m));
    } catch (err) {
      console.error("Role update failed:", err);
      alert("Failed to update staff role.");
    } finally {
      setActionLoading(null);
    }
  };

  if (currentUser?.role !== StaffRole.ADMIN && currentUser?.role !== StaffRole.SUPER_ADMIN) {
    return (
      <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-[3rem] border border-red-100 dark:border-red-900/30 shadow-xl max-w-2xl mx-auto mt-10 transition-colors">
        <i className="fas fa-lock text-5xl text-red-500 mb-6"></i>
        <h3 className="text-2xl font-serif font-bold text-slate-900 dark:text-slate-100">Access Restricted</h3>
        <p className="text-slate-500 dark:text-slate-400 mt-3 font-medium leading-relaxed">Only authorized administrators are permitted to manage firm personnel.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 max-w-7xl mx-auto">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-[0_16px_48px_-12px_rgba(0,0,0,0.08)] border border-slate-100 dark:border-slate-800 overflow-hidden transition-colors">
        <div className="px-10 py-10 border-b border-slate-50 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-colors">
          <div>
            <h3 className="font-serif text-3xl font-bold text-slate-900 dark:text-slate-100">Firm Personnel</h3>
            <p className="text-[10px] text-slate-400 dark:text-slate-600 font-bold uppercase tracking-[0.25em] mt-3">Secure Credential Management Terminal</p>
          </div>
          <button 
            onClick={() => setIsAdding(true)}
            className="px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 shadow-2xl shadow-slate-200 dark:shadow-none group"
          >
            <i className="fas fa-user-plus group-hover:scale-110 transition-transform"></i> Invite New Member
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-950/50 transition-colors">
                <th className="px-10 py-6 text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em] w-1/3">Professional Identity</th>
                <th className="px-10 py-6 text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em]">Office Email</th>
                <th className="px-10 py-6 text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em]">Clearance Level</th>
                <th className="px-10 py-6 text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800 transition-colors">
              {staff.map(member => (
                <tr key={member.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-950/70 transition-colors group">
                  <td className="px-10 py-8">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 dark:text-slate-600 font-bold font-serif text-xl border border-white dark:border-slate-700 shadow-sm transition-colors">
                        {member.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-serif font-bold text-slate-900 dark:text-slate-100 text-xl">{member.name}</div>
                        <div className="text-[10px] text-slate-400 dark:text-slate-600 mt-1 font-bold uppercase tracking-widest">Onboarded {new Date(member.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-10 py-8">
                    <span className="text-sm font-semibold text-slate-600 dark:text-slate-400 bg-slate-100/50 dark:bg-slate-950/50 px-3 py-1.5 rounded-xl border border-slate-100 dark:border-slate-800 transition-colors">
                      {member.email}
                    </span>
                  </td>
                  <td className="px-10 py-8">
                    <div className="relative inline-block group/select">
                      <select 
                        value={member.role}
                        onChange={(e) => handleRoleChange(member, e.target.value as StaffRole)}
                        disabled={member.id === currentUser?.id || actionLoading?.startsWith('updating')}
                        className="text-[11px] font-bold uppercase tracking-wider bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-300 rounded-xl px-5 py-3 outline-none focus:border-slate-900 dark:focus:border-amber-600 focus:ring-4 focus:ring-slate-900/5 transition-all cursor-pointer appearance-none pr-10 disabled:opacity-50"
                      >
                        {Object.values(StaffRole).map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-slate-300 dark:text-slate-700 pointer-events-none group-hover/select:text-slate-900 dark:group-hover/select:text-amber-500 transition-colors"></i>
                    </div>
                  </td>
                  <td className="px-10 py-8 text-right">
                    <div className="flex justify-end items-center gap-2">
                      {actionLoading === `deleting-${member.id}` ? (
                        <div className="w-10 h-10 flex items-center justify-center">
                          <i className="fas fa-circle-notch animate-spin text-slate-300 dark:text-slate-700"></i>
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleDelete(member.id)}
                          disabled={member.id === currentUser?.id || !!actionLoading}
                          className="w-12 h-12 rounded-2xl flex items-center justify-center text-slate-300 dark:text-slate-700 hover:text-red-600 dark:hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-0 transition-all border border-transparent hover:border-red-100 dark:hover:border-red-900/30"
                          title="Revoke Access"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {loading && (
             <div className="p-32 text-center text-slate-400">
               <i className="fas fa-circle-notch animate-spin text-4xl mb-6 text-slate-200 dark:text-slate-800"></i>
               <p className="text-[10px] font-bold uppercase tracking-[0.3em]">Querying Personnel Database...</p>
             </div>
          )}
        </div>
      </div>

      {isAdding && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm p-4 animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/50 dark:border-white/5">
            <div className="px-12 py-10 bg-slate-900 dark:bg-slate-950 text-white flex justify-between items-center relative z-10 transition-colors">
              <h4 className="font-serif text-3xl font-bold">Issue Credentials</h4>
              <button 
                onClick={() => setIsAdding(false)} 
                className="w-12 h-12 rounded-2xl flex items-center justify-center bg-white/10 hover:bg-white/20 transition-all"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleAddStaff} className="p-12 space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Legal Full Name</label>
                <input 
                  type="text" required
                  value={newStaff.name}
                  onChange={e => setNewStaff({...newStaff, name: e.target.value})}
                  className="w-full px-6 py-5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 outline-none transition-all font-semibold placeholder-slate-300 dark:placeholder-slate-700"
                  placeholder="e.g. Maria Gonzalez"
                />
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Official Work Email</label>
                <input 
                  type="email" required
                  value={newStaff.email}
                  onChange={e => setNewStaff({...newStaff, email: e.target.value})}
                  className="w-full px-6 py-5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 outline-none transition-all font-semibold placeholder-slate-300 dark:placeholder-slate-700"
                  placeholder="name@lluislaw.com"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Initial Clearance</label>
                  <select 
                    value={newStaff.role}
                    onChange={e => setNewStaff({...newStaff, role: e.target.value as StaffRole})}
                    className="w-full px-6 py-5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-300 font-bold text-[10px] uppercase tracking-widest appearance-none cursor-pointer outline-none transition-colors"
                  >
                    {Object.values(StaffRole).map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest ml-1">Access Pin</label>
                  <input 
                    type="text" required
                    value={newStaff.password}
                    onChange={e => setNewStaff({...newStaff, password: e.target.value})}
                    className="w-full px-6 py-5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-mono text-lg tracking-[0.2em] outline-none transition-colors"
                  />
                </div>
              </div>
              
              <button 
                type="submit"
                disabled={actionLoading === 'adding'}
                className="w-full py-6 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-2xl hover:bg-slate-800 dark:hover:bg-slate-100 transition-all uppercase tracking-widest text-[10px] flex items-center justify-center gap-4 disabled:opacity-50 shadow-2xl shadow-slate-200 dark:shadow-none"
              >
                {actionLoading === 'adding' ? <i className="fas fa-circle-notch animate-spin"></i> : <i className="fas fa-shield-check"></i>}
                Confirm Account Initialization
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
